import { clsx } from 'clsx';
import { useMemo } from 'react';

import type { AccordionProps } from './accordion.types';
import { AccordionContent } from './accordionContent';
import { AccordionAutoScrollContext } from './accordionContext';
import { AccordionHeading } from './accordionHeading';
import { AccordionIndicator } from './accordionIndicator';
import { AccordionItem } from './accordionItem';
import { AccordionSummary } from './accordionSummary';

const BaseAccordion = ({
  autoScrollMargin,
  children,
  className,
  'data-testid': testId,
  id,
  skipInitialScroll,
  style,
}: AccordionProps) => {
  const value = useMemo(
    () => ({ autoScrollMargin, skipInitialScroll }),
    [autoScrollMargin, skipInitialScroll]
  );

  return (
    <AccordionAutoScrollContext.Provider value={value}>
      <div
        className={clsx(className)}
        id={id}
        data-testid={testId}
        style={style}
      >
        {children}
      </div>
    </AccordionAutoScrollContext.Provider>
  );
};

/**
 * Accordion is a collapsible content component that allows users to expand and collapse sections.
 *
 * Note: when using long duration animation in iframe, it can lead to laggy UI and low framerate
 * due to the iframe getting resized as the content changes. This can be reduced by decreasing
 * transition duration.
 */
export const Accordion = Object.assign(BaseAccordion, {
  Content: AccordionContent,
  Heading: AccordionHeading,
  Indicator: AccordionIndicator,
  Item: AccordionItem,
  Summary: AccordionSummary,
});
